// The data contract every page of Relationship Radar reads.
//
// Peter's sources (Monday board, Google Contacts, LinkedIn, conference lists, touch log, past deals) are parsed
// and merged in the browser (shared/ingest.ts) into one Network. The browser gzips it, the Worker encrypts it
// and keeps it in R2, and every page (This week, Deals, Lists, People) and the MCP endpoint read it back.
//
// Field names are short on purpose: 5,000 contacts with touch history is several megabytes of JSON.

export const NETWORK_VERSION = 2;

/** A private life event or status found in notes, texts or the CRM. Sensitive ones put a person on hold. */
export interface Flag {
  flag: string; // "deceased" | "serious illness" | "divorce" | "loss of child" | "inactive" | "opted out" | "retired" | "tapped out" | "sold company" | "new role" | "family milestone"
  date: string | null; // ISO date when known
  source: string; // "notes" | "touch log" | "Monday status" | ...
  text: string; // the words that triggered it, trimmed
}

/** One past deal this person was pitched on. */
export interface PastPitch {
  deal: string; // deal_id
  outcome: string; // "Invested" | "Passed" | "No response"
  amount: number;
  date: string; // ISO date pitched
}

export interface EventAttendance {
  name: string;
  date: string; // ISO date of the event
}

/** [date, channel, direction "I" (they reached out) | "O" (Peter did), summary] */
export type RecentTouch = [string, string, "I" | "O", string];

export interface Contact {
  /** Position in this build, "C00042". Changes between builds: never store it. */
  id: string;
  /** Stable across builds: "e:" + primary email, else "n:" + first|last|company (all lowercase). Notes, holds,
   *  deal responses and list decisions are keyed by this. */
  k: string;
  n: string; // full name
  co: string; // company
  lco: string; // company LinkedIn shows, when it differs (possible job change)
  ti: string; // title
  ty: string; // investor type, see INVESTOR_TYPES ("Unknown" when nothing says)
  tys: string; // where the type came from: "crm" | "notes" | "claude" | "missing"
  ci: string; // city
  st: string; // state / region code
  rg: string; // region, see REGIONS
  se: string[]; // sectors, see SECTORS
  ses: string; // where the sectors came from: "crm" | "notes" | "claude" | "missing"
  dt: string[]; // deal types they do, see DEAL_TYPES
  cmin: number; // check size, USD
  cmax: number;
  cs: string; // where the check size came from: "crm" | "notes" | "estimated from type" | "not an investor" | "unknown"
  rs: number; // relationship strength 0-5 (0 = not in the CRM, e.g. a conference-only name)
  hm: string; // how they met
  em: string; // primary email
  ph: string;
  no: string; // all notes joined
  src: string[]; // sources this person appears in: "Monday", "Google", "LinkedIn", "Conference", "Touch log"
  li: string; // LinkedIn URL
  fl: Flag[];
  cf: EventAttendance[];
  pa: PastPitch[];
  bu: string[]; // sectors they got burned in
  ji: { sector: string; date: string | null } | null; // just invested elsewhere
  ib: number[]; // inbound touch days, as days since Network.epoch, sorted
  ob: number[]; // outbound touch days
  rt: RecentTouch[]; // last 5 touches, oldest first
  np?: string[]; // things they said no to ("no real estate", "no venture")
  as?: { sector: string; date: string | null }[]; // sectors they asked Peter for deals in, from the full touch log
}

export interface PastDealRow {
  deal_id: string;
  name: string;
  sectors: string; // pipe-separated
  structure: string;
  raise_usd: string;
  launch_date: string;
  close_date: string;
  sponsor: string;
}

export interface EventInfo {
  name: string;
  date: string;
  file?: string;
}

export interface SourceSummary {
  id: string;
  kind: SourceKind;
  label: string; // "Monday board: Investor Network - Peter B."
  rows: number;
}

export interface Network {
  version: typeof NETWORK_VERSION;
  generated: string; // ISO date the radar is "as of" (build day)
  epoch: string; // day zero for ib/ob
  contacts: Contact[];
  past_deals: PastDealRow[];
  events: EventInfo[];
  sectors: string[];
  report: Record<string, number>;
  sources: SourceSummary[];
}

export type SourceKind = "crm" | "contacts" | "linkedin" | "conference" | "touches" | "deals" | "participation";

export const SECTORS = [
  "Food & Beverage", "CPG", "Restaurants & Hospitality", "Food Logistics & Cold Chain", "Healthcare Services",
  "Business Services", "Consumer Services", "Industrials & Manufacturing", "Automotive", "Aerospace & Defense",
  "Tech-Enabled Services", "Legal & Professional Services", "Energy", "Software", "Sports & Recreation",
];

export const INVESTOR_TYPES = [
  "Family office", "Multi-family office", "UHNW individual", "HNW individual", "Independent sponsor", "PE fund GP",
  "LP / fund allocator", "Operator / executive", "Banker / advisor", "Service provider", "Unknown",
];

export const DEAL_TYPES = ["Direct / co-invest", "SPV", "Independent sponsor deals", "Fund LP"];

export const REGIONS = ["Mountain West", "Southwest", "Midwest", "Texas", "Northeast", "West Coast", "Southeast", "Other"];

/** Life events that put a person on hold, and for how many days after the event. */
export const SENSITIVE_WINDOW_DAYS: Record<string, number> = {
  "deceased": 100000,
  "loss of child": 730,
  "serious illness": 365,
  "divorce": 365,
  "inactive": 100000,
  "opted out": 100000,
};

/** The private overlay Peter keeps on top of the network (D1 table notes), keyed by Contact.k. */
export interface PrivateNote {
  note: string;
  hold: boolean;
  noPitch: boolean;
  sectors?: string[]; // sectors Peter says they invest in, added to the CRM's
  updatedAt?: string;
}

/** A deal teaser Peter is working (D1 table deals). */
export interface Deal {
  deal_id: string;
  codename: string;
  received: string;
  one_liner: string;
  summary: string;
  sponsor: string;
  deal_type: string;
  structure: string;
  sectors: string[];
  adjacent: string[];
  raise: number;
  min_ticket: number;
  region: string;
  timeline_months: number;
  sponsor_prior_raises: number;
  retainer_offered: boolean;
  first_time_fund: boolean;
  friend_gp?: boolean;
  is_fund: boolean;
  values_flags: string[];
  culture_gut: number;
}

export type DealResponse = "interested" | "later" | "passed";

/** Everything private Peter has added, as the app loads it. */
export interface PrivateState {
  notes: Record<string, PrivateNote>; // by Contact.k
  responses: Record<string, Record<string, DealResponse>>; // deal_id -> k -> response
  contacted: Record<string, string>; // k -> ISO date Peter marked "contacted"
  deals: Deal[];
}

export function contactKey(email: string, first: string, last: string, company: string): string {
  const e = (email || "").trim().toLowerCase();
  if (e) return "e:" + e;
  return "n:" + [first, last, company].map((s) => (s || "").trim().toLowerCase()).join("|");
}
